import { CDashboardModuleTabComponent, ComponentProps, Context, PageContext, UIComponent } from "../types";
import { CSSProperties, useMemo, useState, useContext, useEffect } from "react";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import DashboardComponent from "./DashboardComponent";
import { Icon, Paper, Tabs } from "@mui/material";
import ExecutionTypeContext, { Mode } from "../ExecutionTypeContext";
import DashboardPageContext from "../DashboardPageContext";

function processNestedComponents(pageContext: PageContext, component: CDashboardModuleTabComponent, evaluteScript: (script: string, breakOnFail?: boolean) => unknown | null): UIComponent[] {
	const list = [] as UIComponent[];
	component.childern.forEach((c) => {
		if (c.type === "MapComponents") {
			const { context, params, updateContext, activatePageLoader } = pageContext;
			const childList = evaluteScript(c.properties["binding"]) as CDashboardModuleTabComponent[];
			if (childList)
				childList.map((_item: unknown, i: number) => {
					const newContext = { ...context };
					newContext[c.properties["variable"]] = i;
					const nestedPageContext = { context: newContext, activatePageLoader, params: params, updateContext: (newValue: Context) => updateContext(newValue) as PageContext };
					processNestedComponents(nestedPageContext, c, evaluteScript).map((nc, i) =>
						list.push({
							component: nc.component,
							jsx: (
								<DashboardPageContext.Provider key={i} value={nestedPageContext}>
									{nc.jsx}
								</DashboardPageContext.Provider>
							),
						}),
					);
				});
		} else list.push({ component: c, jsx: <DashboardComponent component={c} /> });
	});
	return list;
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
	style: CSSProperties;
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index } = props;

	return (
		<div role="tabpanel" style={{ ...{ display: value === index ? "flex" : "none", flex: 1 }, ...props.style }} hidden={value !== index}>
			{children}
		</div>
	);
}

function Component({ component, evaluteScript, evalJsText }: ComponentProps) {
	const pageContext = useContext(DashboardPageContext);
	const exeMode = useContext(ExecutionTypeContext);
	const [value, setValue] = useState(0);
	const isVertical = useMemo(() => {
		return (component.properties["orientation"] || "horizontal") === "horizontal" ? false : true;
	}, [component]);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};

	const tabPanels = useMemo((): UIComponent[] => {
		const processedTree = processNestedComponents(pageContext, component, evaluteScript);
		const tabs = [] as UIComponent[];
		processedTree.forEach((c) => {
			if (c.component.type === "TabPanel") tabs.push(c);
		});
		return tabs;
	}, [component, pageContext, evaluteScript]);

	useEffect(() => {
		if (tabPanels && tabPanels.length > 0) if (value > tabPanels.length - 1) setValue(tabPanels.length - 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tabPanels]);

	return (
		<Box
			sx={{
				display: "flex",
				flex: component.properties["flex"] ? component.properties["flex"] : "initial",
				typography: "body1",
				border: exeMode?.mode === Mode.DESIGN && exeMode.activeComponent?.id === component.id ? "1px dashed red" : "initial",
			}}
			key={component.id}
		>
			{tabPanels && tabPanels.length > 0 && value < tabPanels.length && (
				<Box sx={{ display: "flex", gap: 1, flex: 1, boxShadow: "none", flexDirection: isVertical ? "row" : "column" }}>
					<Paper elevation={component.properties["tabElevation"] ? parseInt(component.properties["tabElevation"]) : 1}>
						<Tabs
							value={value}
							variant={(component.properties["variant"] as "scrollable" | "standard" | "fullWidth") || "scrollable"}
							scrollButtons={!component.properties["variant"] || component.properties["variant"] === "scrollable"}
							allowScrollButtonsMobile
							onChange={handleChange}
							orientation={isVertical ? "vertical" : "horizontal"}
							sx={{
								"& .MuiTabs-scrollButtons.Mui-disabled": { opacity: "0.3" },
								"& .MuiTabs-flexContainer": { alignItems: "stretch" },
								"& .MuiTab-labelIcon": { minHeight: "50px", display: "flex", flexDirection: "row", justifyContent: "flex-start", borderBottom: isVertical ? 1 : 0, borderColor: "divider" },
								"& .MuiButtonBase-root": { minWidth: "50px" },
							}}
						>
							{tabPanels.map((tab, i) => {
								// eslint-disable-next-line @typescript-eslint/no-unused-vars
								const tabPageContext = tab.jsx.props.value ? tab.jsx.props.value : pageContext;
								return (
									<Tab
										key={i}
										title={evalJsText(tab.component.properties["tooltip"], true, tabPageContext) as string}
										sx={{ textTransform: "initial" }}
										icon={tab.component.properties["icon"] && <Icon>{tab.component.properties["icon"]}</Icon>}
										iconPosition="start"
										label={evalJsText(tab.component.properties["label"], true, tabPageContext)}
										value={i}
									/>
								);
							})}
						</Tabs>
					</Paper>
					<Box sx={{ flex: 1, display: "flex" }}>
						{tabPanels.map((tab, i) => {
							const style = {} as CSSProperties;
							if (tab.component.properties) {
								style.padding = isVertical ? "0px" : "10px 0px 10px 0px";
								if (tab.component.properties["width"]) style.width = tab.component.properties["width"];
								if (tab.component.properties["height"]) style.height = tab.component.properties["height"];
								if (tab.component.properties["padding"]) style.padding = tab.component.properties["padding"];
								if (tab.component.properties["backgroundColor"]) style.backgroundColor = tab.component.properties["backgroundColor"];
							}
							return (
								<CustomTabPanel key={i} value={value} index={i} style={style}>
									{tab.jsx}
								</CustomTabPanel>
							);
						})}
					</Box>
				</Box>
			)}
		</Box>
	);
}

export default Component;
